// UF-01: the kg a bin normally weighs, worked out from the business's own recent
// tips (Jason, 25 Oct 2026: "from each business's own recent tips"). Pure helper:
// the read lives in TipRangeHint.tsx.

/** One past delivery tip, already tagged with its bin type. */
export interface TipSample {
  /** Stable group key: the delivery's bin type id, else its container type. */
  key: string
  /** What the screen calls that bin type. */
  label: string
  netKg: number
  binQty: number
}

export interface BinRange {
  label: string
  lo: number
  hi: number
  count: number
}

/** No warning until a bin type has this many tips behind it. */
export const MIN_TIPS_FOR_RANGE = 20

/** Linear-interpolated percentile (p in 0..1) of an ascending-sorted list. */
export function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return NaN
  const idx = (sorted.length - 1) * p
  const lo = Math.floor(idx)
  const hi = Math.ceil(idx)
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo)
}

/** The band's edges: the 1st and 99th percentile (Jason, 26 Oct). A 5th..95th
 *  band flagged about 1 correct tip in 10, every other day at ~6 tips a day. */
export const RANGE_LO_PCT = 0.01
export const RANGE_HI_PCT = 0.99

/** p01..p99 of net kg per bin, for each bin type that has enough tips. */
export function binRanges(samples: TipSample[], minTips = MIN_TIPS_FOR_RANGE): Map<string, BinRange> {
  const groups = new Map<string, { label: string; perBin: number[] }>()
  for (const s of samples) {
    if (!(s.binQty > 0) || !Number.isFinite(s.netKg) || s.netKg <= 0) continue
    const g = groups.get(s.key) ?? { label: s.label, perBin: [] }
    g.perBin.push(s.netKg / s.binQty)
    groups.set(s.key, g)
  }
  const out = new Map<string, BinRange>()
  for (const [key, g] of groups) {
    if (g.perBin.length < minTips) continue
    const sorted = [...g.perBin].sort((a, b) => a - b)
    out.set(key, {
      label: g.label,
      lo: Math.round(percentile(sorted, RANGE_LO_PCT)),
      hi: Math.round(percentile(sorted, RANGE_HI_PCT)),
      count: sorted.length,
    })
  }
  return out
}

/**
 * The amber line for a typed tip, or null when nothing looks odd. Never blocks.
 * netKg is the net the server will save (null when it is not known).
 */
export function kgPerBinWarning(range: BinRange | undefined, netKg: number | null | undefined, binQty: number): string | null {
  if (!range || netKg == null || !(binQty > 0) || !(netKg > 0)) return null
  const perBin = netKg / binQty
  if (perBin >= range.lo && perBin <= range.hi) return null
  return `${perBin.toFixed(1)} kg per bin. Bins of this type usually run ${range.lo} to ${range.hi} kg. Check the gross.`
}

/** The group a delivery's tips belong to: its bin type, else its container type. */
export function binGroupKey(binTypeId: string | null | undefined, containerType: string | null | undefined): string | null {
  if (binTypeId) return `bt:${binTypeId}`
  return containerType ? `ct:${containerType}` : null
}

/** A tipping_log row joined to its delivery, as the hint reads it. */
export interface TipRow {
  bin_quantity: number
  net_weight_kg: number
  deliveries: {
    fruit_bin_type_id: string | null
    fruit_container_type: string | null
    bin_types: { label: string | null } | null
  } | null
}

/** Rows to samples; a row whose delivery has no bin type or container is dropped. */
export function rowsToSamples(rows: TipRow[]): TipSample[] {
  const out: TipSample[] = []
  for (const r of rows) {
    const d = r.deliveries
    const key = binGroupKey(d?.fruit_bin_type_id, d?.fruit_container_type)
    if (!d || !key) continue
    out.push({
      key,
      label: d.bin_types?.label || (d.fruit_container_type ?? 'this bin type'),
      netKg: Number(r.net_weight_kg),
      binQty: Number(r.bin_quantity),
    })
  }
  return out
}
